/**
 * OrderHistory.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatDateAtlantic, formatOrderDate } from '../utils/dateFormat';
import { parseInquiryThread } from '../utils/orderHistoryDisplay';
import OrderStatusTimeline from './OrderStatusTimeline';
import OrderCollapsibleSection from './OrderCollapsibleSection';
import './OrderHistory.css';
import './OrderCollapsibleSection.css';

const SECTION_IDS = {
  PAYMENT: 'payment',
  ITEMS: 'items',
  HISTORY: 'history',
  CONVERSATION: 'conversation',
};

function getInquiryThread(order) {
  return parseInquiryThread(order?.inquiry_thread);
}

function getDefaultCollapsedSections(order) {
  const hasConversation = getInquiryThread(order).length > 0;
  return {
    [SECTION_IDS.PAYMENT]: true,
    [SECTION_IDS.ITEMS]: false,
    [SECTION_IDS.HISTORY]: true,
    [SECTION_IDS.CONVERSATION]: !hasConversation,
  };
}

function formatShippingAddress(address) {
  if (!address || typeof address !== 'object') return '';
  return [
    address.street,
    address.street2,
    [address.city, address.province || address.state, address.postalCode].filter(Boolean).join(', '),
    address.countryName || address.country,
  ].filter(Boolean).join('\n');
}

const OrderHistory = () => {
  const { currentUser } = useAuth();
  const userEmail = currentUser?.email || '';
  const [orders, setOrders] = useState([]);
  const [cartInquiries, setCartInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [expandedCartInquiry, setExpandedCartInquiry] = useState(null);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [inquiryQuestion, setInquiryQuestion] = useState('');
  const [submittingInquiry, setSubmittingInquiry] = useState(false);
  const [inquiryError, setInquiryError] = useState(null);
  const [inquirySuccess, setInquirySuccess] = useState(null);
  const deepLinkHandled = useRef(false);
  const [collapsedSections, setCollapsedSections] = useState({});

  const getSectionKey = (orderNumber, sectionId) => `${orderNumber}:${sectionId}`;

  const isSectionCollapsed = (order, sectionId) => {
    const key = getSectionKey(order.order_number, sectionId);
    if (Object.prototype.hasOwnProperty.call(collapsedSections, key)) {
      return collapsedSections[key];
    }
    return getDefaultCollapsedSections(order)[sectionId];
  };

  const toggleSection = (order, sectionId) => {
    const key = getSectionKey(order.order_number, sectionId);
    setCollapsedSections((prev) => ({
      ...prev,
      [key]: !isSectionCollapsed(order, sectionId),
    }));
  };

  const refreshOrderFromServer = useCallback(async (orderId) => {
    if (!userEmail) return null;

    try {
      const response = await fetch(`/api/orders/user/${encodeURIComponent(userEmail)}`);
      if (!response.ok) return null;

      const data = await response.json();
      const freshOrder = (data.orders || []).find((o) => o.id === orderId);
      if (!freshOrder) return null;

      setOrders((prev) => prev.map((order) => (
        order.id === freshOrder.id
          ? { ...order, ...freshOrder, items: order.items }
          : order
      )));

      return freshOrder;
    } catch (err) {
      console.warn('Could not refresh order:', err);
      return null;
    }
  }, [userEmail]);

  const fetchCartInquiries = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setCartInquiries([]);
      return;
    }

    try {
      const response = await fetch('/api/orders/cart-inquiries', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          setCartInquiries([]);
          return;
        }
        throw new Error('Failed to fetch cart inquiries');
      }

      const data = await response.json();
      setCartInquiries(data.inquiries || []);
    } catch (err) {
      console.error('Error fetching cart inquiries:', err);
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    if (!userEmail) {
      return;
    }

    try {
      const response = await fetch(`/api/orders/user/${encodeURIComponent(userEmail)}`);

      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }

      const data = await response.json();
      let orders = data.orders || [];

      // Enrich orders with bundle data if missing (backward compatibility)
      orders = await Promise.all(orders.map(async (order) => {
        if (!order.items || !Array.isArray(order.items)) {
          return order;
        }

        const enrichedItems = await Promise.all(order.items.map(async (item) => {
          // Skip if already has bundle data or is a packing item
          if (item.bundleProducts || item.isPartOfBundle || !item.id) {
            return item;
          }

          // Check if this might be a bundle by fetching product data
          try {
            const productResponse = await fetch(`/api/products/${item.id}`);
            if (productResponse.ok) {
              const product = await productResponse.json();
              if (product.is_bundle === 1 || product.is_bundle === true) {
                let bundleProducts = [];
                if (product.bundle_products) {
                  const productIds = product.bundle_products.split(',').filter(id => id.trim());
                  try {
                    const bundleProductPromises = productIds.map(id =>
                      fetch(`/api/products/${id.trim()}`)
                        .then(res => res.ok ? res.json() : null)
                        .catch(() => null)
                    );
                    bundleProducts = (await Promise.all(bundleProductPromises))
                      .filter(p => p !== null)
                      .map(p => ({
                        id: p.id,
                        name: p.name,
                        title: p.name,
                        price_cents: p.price_cents || p.price || 0,
                        price: p.price_cents || p.price || 0
                      }));
                  } catch (err) {
                    console.warn('Could not fetch bundle product details:', err);
                    bundleProducts = productIds.map(id => ({ id: id.trim() }));
                  }
                }
                return {
                  ...item,
                  isBundle: true,
                  is_bundle: true,
                  bundleProducts: bundleProducts,
                  bundleDiscount: product.bundle_discount || 0,
                  originalPrice: product.price_cents || product.price || 0
                };
              }
            }
          } catch (err) {
            // Silently fail - just return original item
            console.warn('Could not fetch product data for item:', item.id, err);
          }

          return item;
        }));

        return { ...order, items: enrichedItems };
      }));

      setOrders(orders);

      // Debug: Log orders to see if variant data is present
      console.log('📦 Orders loaded:', orders.map(o => ({
        orderNumber: o.order_number,
        items: o.items.map(i => ({
          name: i.name,
          selectedSize: i.selectedSize,
          selectedColor: i.selectedColor
        }))
      })));

      setError(null);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err.message);
    }
  }, [userEmail]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchOrders(), fetchCartInquiries()]);
    setLoading(false);
  }, [fetchOrders, fetchCartInquiries]);

  useEffect(() => {
    if (!userEmail) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([fetchOrders(), fetchCartInquiries()]).finally(() => setLoading(false));
  }, [fetchOrders, fetchCartInquiries, userEmail]);

  // Handle deep-link: ?order=ORD-XXXX&action=inquiry from email buttons
  useEffect(() => {
    if (deepLinkHandled.current || orders.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const orderNum = params.get('order');
    const action = params.get('action');
    if (!orderNum) return;

    deepLinkHandled.current = true;

    const openDeepLinkedOrder = async () => {
      const target = orders.find(
        (o) => o.order_number === orderNum || String(o.order_number) === orderNum
      );
      if (!target) return;

      const fresh = await refreshOrderFromServer(target.id);
      const orderToUse = fresh || target;
      setExpandedOrder(orderNum);
      setCollapsedSections((prev) => ({
        ...prev,
        [`${orderNum}:${SECTION_IDS.CONVERSATION}`]: false,
      }));

      if (action === 'inquiry') {
        setSelectedOrder(orderToUse);
        setInquiryQuestion('');
        setInquiryError(null);
        setInquirySuccess(null);
        setShowInquiryModal(true);
      }
    };

    openDeepLinkedOrder();

    // Clean up URL params without reload
    const cleanUrl = window.location.pathname + '?tab=orders';
    window.history.replaceState({}, '', cleanUrl);
  }, [orders, refreshOrderFromServer]);

  const toggleOrderDetails = async (order) => {
    const orderNumber = order.order_number;
    if (expandedOrder === orderNumber) {
      setExpandedOrder(null);
      return;
    }

    setExpandedOrder(orderNumber);
    await refreshOrderFromServer(order.id);
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'completed': return 'status-completed';
      case 'processing': return 'status-processing';
      case 'shipped': return 'status-shipped';
      case 'delivered': return 'status-completed';
      case 'cancelled': return 'status-cancelled';
      case 'inquiry': return 'status-inquiry';
      default: return '';
    }
  };

  const openInquiryModal = (order) => {
    setSelectedOrder(order);
    setInquiryQuestion('');
    setInquiryError(null);
    setInquirySuccess(null);
    setShowInquiryModal(true);
  };

  const closeInquiryModal = () => {
    setShowInquiryModal(false);
    setSelectedOrder(null);
    setInquiryQuestion('');
    setInquiryError(null);
    setInquirySuccess(null);
  };

  const submitInquiry = async () => {
    if (!inquiryQuestion.trim()) {
      setInquiryError('Please enter your question');
      return;
    }

    setSubmittingInquiry(true);
    setInquiryError(null);

    try {
      const response = await fetch(`/api/orders/${selectedOrder.id}/inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          question: inquiryQuestion.trim(),
          customerEmail: currentUser.email
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit inquiry');
      }

      const data = await response.json();

      // Update the order in the list with new inquiry thread
      setOrders(orders.map(order =>
        order.id === selectedOrder.id
          ? {
            ...order,
            inquiry_thread: JSON.stringify(data.inquiryThread),
            notes_history: data.notes_history
              ? JSON.stringify(data.notes_history)
              : order.notes_history,
            status: 'inquiry',
          }
          : order
      ));

      setCollapsedSections((prev) => ({
        ...prev,
        [`${selectedOrder.order_number}:${SECTION_IDS.CONVERSATION}`]: false,
      }));

      setInquirySuccess('Your inquiry has been submitted successfully! We\'ll respond shortly.');

      // Close modal after 2 seconds
      setTimeout(() => {
        closeInquiryModal();
        fetchOrders(); // Refresh orders to get latest data
      }, 2000);

    } catch (err) {
      console.error('Error submitting inquiry:', err);
      setInquiryError(err.message);
    } finally {
      setSubmittingInquiry(false);
    }
  };

  if (loading) {
    return (
      <div className="order-history">
        <h2>📦 Orders & Cart Inquiries</h2>
        <div className="loading-spinner">Loading your account history...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="order-history">
        <h2>📦 Orders & Cart Inquiries</h2>
        <div className="error-message">
          <p>❌ Failed to load orders: {error}</p>
          <button onClick={refreshAll} className="retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  if (orders.length === 0 && cartInquiries.length === 0) {
    return (
      <div className="order-history">
        <h2>📦 Orders & Cart Inquiries</h2>
        <div className="no-orders">
          <div className="no-orders-icon">📦</div>
          <p>You haven&apos;t placed any orders or sent any cart inquiries yet.</p>
          <a href="/" className="shop-now-btn">Start Shopping</a>
        </div>
      </div>
    );
  }

  return (
    <div className="order-history">
      <div className="order-history-header">
        <div>
          <h2>📦 Orders & Cart Inquiries</h2>
          <p className="order-count">
            {orders.length} {orders.length === 1 ? 'order' : 'orders'}
            {cartInquiries.length > 0 && (
              <> · {cartInquiries.length} cart {cartInquiries.length === 1 ? 'inquiry' : 'inquiries'}</>
            )}
          </p>
        </div>
        <button
          onClick={refreshAll}
          className="refresh-orders-btn"
          disabled={loading}
          title="Refresh order list"
        >
          {loading ? '⏳ Refreshing...' : '🔄 Refresh'}
        </button>
      </div>

      {cartInquiries.length > 0 && (
        <section className="cart-inquiries-section">
          <h3 className="cart-inquiries-heading">📧 Cart inquiries</h3>
          <p className="cart-inquiries-intro">
            Carts you emailed from checkout. No payment was taken — Paul will follow up if you want to complete the order.
          </p>
          <div className="orders-list">
            {cartInquiries.map((inquiry) => {
              const isExpanded = expandedCartInquiry === inquiry.reference_id;
              const shippingText = formatShippingAddress(inquiry.shipping_address);

              return (
                <div key={inquiry.id} className="order-card cart-inquiry-card">
                  <div
                    className="order-header"
                    onClick={() => setExpandedCartInquiry(isExpanded ? null : inquiry.reference_id)}
                  >
                    <div className="order-main-info">
                      <div className="order-number-section">
                        <span className="order-label">Cart inquiry</span>
                        <span className="order-number">{inquiry.reference_id}</span>
                      </div>
                      <div className="order-date">
                        <span className="date-icon">📅</span>
                        <span>{formatOrderDate(inquiry.created_at_full || inquiry.created_at)}</span>
                      </div>
                    </div>

                    <div className="order-summary">
                      <span className="order-status status-cart-inquiry">EMAIL SENT</span>
                      <span className="order-total">${(inquiry.total / 100).toFixed(2)}</span>
                      <span className="expand-icon">{isExpanded ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="order-details">
                      {inquiry.personal_message && (
                        <div className="cart-inquiry-message-block">
                          <h4>Your message</h4>
                          <p>{inquiry.personal_message}</p>
                        </div>
                      )}

                      <OrderCollapsibleSection
                        id="items"
                        title={`📦 Items (${inquiry.items.length})`}
                        collapsed={false}
                        onToggle={() => {}}
                      >
                        <div className="items-list">
                          {inquiry.items.map((item, index) => {
                            const itemPrice = item.unitPrice || item.price_cents || item.price || 0;
                            return (
                              <div key={index} className="order-item">
                                <div className="item-info">
                                  <span className="item-name">{item.name || `Product #${item.id}`}</span>
                                  <span className="item-quantity">Qty: {item.quantity}</span>
                                  {(item.selectedSize || item.selectedColor) && (
                                    <div className="item-variant-note">
                                      {[
                                        item.selectedSize && `Size: ${item.selectedSize}`,
                                        item.selectedColor && `Color: ${item.selectedColor}`,
                                      ].filter(Boolean).join(', ')}
                                    </div>
                                  )}
                                </div>
                                <span className="item-price">
                                  ${((itemPrice * item.quantity) / 100).toFixed(2)}
                                </span>
                              </div>
                            );
                          })}
                        </div>

                        <div className="order-pricing">
                          <div className="pricing-row">
                            <span>Subtotal:</span>
                            <span>${(inquiry.subtotal / 100).toFixed(2)}</span>
                          </div>
                          <div className="pricing-row">
                            <span>Shipping:</span>
                            <span>{inquiry.shipping === 0 ? 'FREE' : `$${(inquiry.shipping / 100).toFixed(2)}`}</span>
                          </div>
                          <div className="pricing-row">
                            <span>Tax:</span>
                            <span>${(inquiry.tax / 100).toFixed(2)}</span>
                          </div>
                          <div className="pricing-row total-row">
                            <span>Estimated total:</span>
                            <span>${(inquiry.total / 100).toFixed(2)}</span>
                          </div>
                        </div>
                      </OrderCollapsibleSection>

                      {shippingText && (
                        <div className="detail-section cart-inquiry-shipping">
                          <h4>🚚 Shipping address</h4>
                          <p className="shipping-address">{shippingText}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {orders.length > 0 && (
        <section className={cartInquiries.length > 0 ? 'orders-section-with-inquiries' : ''}>
          {cartInquiries.length > 0 && (
            <h3 className="orders-section-heading">🛒 Orders</h3>
          )}
          <div className="orders-list">
        {orders.map(order => (
          <div key={order.id} className="order-card">
            <div className="order-header" onClick={() => toggleOrderDetails(order)}>
              <div className="order-main-info">
                <div className="order-number-section">
                  <span className="order-label">Order</span>
                  <span className="order-number">#{order.order_number}</span>
                </div>
                <div className="order-date">
                  <span className="date-icon">📅</span>
                  <span>{formatOrderDate(order.created_at_full || order.created_at)}</span>
                </div>
              </div>

              <div className="order-summary">
                <span className={`order-status ${getStatusColor(order.status)}`}>
                  {order.status.toUpperCase()}
                </span>
                <span className="order-total">${(order.total / 100).toFixed(2)}</span>
                <span className="expand-icon">{expandedOrder === order.order_number ? '▲' : '▼'}</span>
              </div>
            </div>

            {expandedOrder === order.order_number && (
              <div className="order-details">
                {(() => {
                  const inquiryThread = getInquiryThread(order);
                  const visibleItems = order.items.filter((i) => !i.isPartOfBundle);
                  const productCount = visibleItems.length;
                  const totalItemCount = visibleItems.reduce((sum, item) => {
                    const bundleProducts = item.bundleProducts || [];
                    const isBundle = (item.isBundle || item.is_bundle || false) || (bundleProducts.length > 0);
                    const itemsPerProduct = isBundle && bundleProducts.length > 0 ? bundleProducts.length : 1;
                    return sum + (item.quantity * itemsPerProduct);
                  }, 0);

                  return (
                    <>
                      <OrderCollapsibleSection
                        id={SECTION_IDS.PAYMENT}
                        title="💳 Payment & Shipping"
                        collapsed={isSectionCollapsed(order, SECTION_IDS.PAYMENT)}
                        onToggle={() => toggleSection(order, SECTION_IDS.PAYMENT)}
                      >
                        <div className="order-details-grid">
                          <div className="detail-section">
                            <h4>💳 Payment</h4>
                            <p className="payment-method">{order.payment_method}</p>
                            {order.payment_intent_id && (
                              <p className="payment-id" style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '8px' }}>
                                <strong>Stripe ID:</strong> {order.payment_intent_id}
                              </p>
                            )}
                            {order.paypal_order_id && (
                              <p className="payment-id" style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '8px' }}>
                                <strong>PayPal ID:</strong> {order.paypal_order_id}
                              </p>
                            )}
                          </div>

                          <div className="detail-section">
                            <h4>🚚 Shipping Address</h4>
                            <p className="shipping-address">{order.shipping_address}</p>
                          </div>
                        </div>
                      </OrderCollapsibleSection>

                      <OrderCollapsibleSection
                        id={SECTION_IDS.ITEMS}
                        title={`📦 Items & Pricing (${productCount} product${productCount !== 1 ? 's' : ''}, ${totalItemCount} item${totalItemCount !== 1 ? 's' : ''})`}
                        collapsed={isSectionCollapsed(order, SECTION_IDS.ITEMS)}
                        onToggle={() => toggleSection(order, SECTION_IDS.ITEMS)}
                      >
                        <div className="items-list">
                          {order.items.map((item, index) => {
                            const isBundle = item.isBundle || item.is_bundle || false;
                            const isPartOfBundle = item.isPartOfBundle || false;
                            const hasBundleProducts = item.bundleProducts && Array.isArray(item.bundleProducts) && item.bundleProducts.length > 0;
                            const actualIsBundle = isBundle || hasBundleProducts;

                            const bundleProducts = item.bundleProducts || [];
                            const bundleDiscount = item.bundleDiscount || item.bundle_discount || 0;
                            const originalPrice = item.originalPrice || item.original_price || 0;
                            const itemPrice = item.unitPrice || item.price_cents || item.price || 0;

                            let bundleSavings = 0;
                            if (actualIsBundle && bundleDiscount > 0 && originalPrice > 0) {
                              bundleSavings = (originalPrice - itemPrice) * item.quantity;
                            }

                            if (isPartOfBundle && !actualIsBundle) {
                              return null;
                            }

                            return (
                              <div key={index} className="order-item">
                                <div className="item-info">
                                  <span className="item-name">
                                    {item.name || `Product #${item.id}`}
                                    {actualIsBundle && (
                                      <span style={{
                                        background: 'linear-gradient(135deg, #4a90e2, #5b9ceb)',
                                        color: '#ffffff',
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        fontSize: '10px',
                                        fontWeight: '600',
                                        marginLeft: '8px',
                                        display: 'inline-block'
                                      }}>
                                        📦 BUNDLE
                                      </span>
                                    )}
                                  </span>
                                  <span className="item-quantity">Qty: {item.quantity}</span>
                                  {(item.selectedSize || item.selectedColor) && (
                                    <div style={{ fontSize: '12px', color: '#fff', fontWeight: 'bold', marginTop: '4px', backgroundColor: 'rgba(74, 144, 226, 0.8)', padding: '4px 8px', borderRadius: '4px', display: 'inline-block' }}>
                                      {[
                                        item.selectedSize && `Size: ${item.selectedSize}`,
                                        item.selectedColor && `Color: ${item.selectedColor}`
                                      ].filter(Boolean).join(', ')}
                                    </div>
                                  )}
                                  {actualIsBundle && bundleProducts.length > 0 && (
                                    <>
                                      <div style={{
                                        fontSize: '12px',
                                        color: '#6b7280',
                                        marginTop: '4px'
                                      }}>
                                        Includes {bundleProducts.length} product{bundleProducts.length !== 1 ? 's' : ''}
                                      </div>
                                      <div style={{
                                        fontSize: '11px',
                                        color: '#9ca3af',
                                        marginTop: '6px',
                                        paddingLeft: '0.5rem',
                                        borderLeft: '2px solid #e5e7eb'
                                      }}>
                                        {bundleProducts.map((bp, idx) => (
                                          <div key={idx} style={{ marginTop: '2px' }}>
                                            • {bp.name || bp.title || `Product #${bp.id || bp.productId || ''}`}
                                            {(bp.selectedSize || bp.selectedColor) && (
                                              <div style={{ fontSize: '11px', color: '#fff', fontWeight: 'bold', marginTop: '2px', marginLeft: '4px', backgroundColor: 'rgba(74, 144, 226, 0.8)', padding: '2px 6px', borderRadius: '3px', display: 'inline-block' }}>
                                                {[
                                                  bp.selectedSize && `Size: ${bp.selectedSize}`,
                                                  bp.selectedColor && `Color: ${bp.selectedColor}`
                                                ].filter(Boolean).join(', ')}
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </>
                                  )}
                                  {actualIsBundle && bundleSavings > 0 && (
                                    <div style={{
                                      fontSize: '12px',
                                      color: '#10b981',
                                      fontWeight: '600',
                                      marginTop: '4px'
                                    }}>
                                      🎉 Save ${(bundleSavings / 100).toFixed(2)} ({bundleDiscount}% off)
                                    </div>
                                  )}
                                </div>
                                <span className="item-price">
                                  ${((itemPrice * item.quantity) / 100).toFixed(2)}
                                </span>
                              </div>
                            );
                          })}
                        </div>

                        <div className="order-pricing">
                          <div className="pricing-row">
                            <span>Subtotal:</span>
                            <span>${(order.subtotal / 100).toFixed(2)}</span>
                          </div>
                          <div className="pricing-row">
                            <span>Shipping:</span>
                            <span>{order.shipping === 0 ? 'FREE' : `$${(order.shipping / 100).toFixed(2)}`}</span>
                          </div>
                          <div className="pricing-row">
                            <span>Tax:</span>
                            <span>${(order.tax / 100).toFixed(2)}</span>
                          </div>
                          <div className="pricing-row total-row">
                            <span>Total:</span>
                            <span>${(order.total / 100).toFixed(2)}</span>
                          </div>
                        </div>
                      </OrderCollapsibleSection>

                      <OrderCollapsibleSection
                        id={SECTION_IDS.CONVERSATION}
                        title="💬 Conversation"
                        count={inquiryThread.length}
                        highlight={inquiryThread.length > 0}
                        collapsed={isSectionCollapsed(order, SECTION_IDS.CONVERSATION)}
                        onToggle={() => toggleSection(order, SECTION_IDS.CONVERSATION)}
                      >
                        {inquiryThread.length > 0 ? (
                          <div className="inquiry-thread">
                            {inquiryThread.map((message) => (
                              <div key={message.id} className={`inquiry-message ${message.author}`}>
                                <div className="inquiry-message-header">
                                  <span className="inquiry-author">
                                    {message.author === 'customer'
                                      ? '👤 You'
                                      : `🏢 ${message.author_name || 'Cranford Publications'}`}
                                  </span>
                                  <span className="inquiry-date">
                                    {formatDateAtlantic(message.created_at)}
                                  </span>
                                </div>
                                <div className="inquiry-message-body">
                                  {message.message}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="order-collapsible-empty">
                            No messages yet. Use the button below to ask a question about this order.
                          </p>
                        )}

                        <div className="order-actions conversation-actions">
                          <button
                            className="inquiry-btn"
                            onClick={() => openInquiryModal(order)}
                          >
                            ❓ {inquiryThread.length > 0 ? 'Continue Conversation' : 'Ask a Question'}
                          </button>
                        </div>
                      </OrderCollapsibleSection>

                      <OrderCollapsibleSection
                        id={SECTION_IDS.HISTORY}
                        title="🕘 Order Status History"
                        collapsed={isSectionCollapsed(order, SECTION_IDS.HISTORY)}
                        onToggle={() => toggleSection(order, SECTION_IDS.HISTORY)}
                      >
                        <OrderStatusTimeline
                          order={order}
                          notesHistory={order.notes_history}
                          inquiryThread={order.inquiry_thread}
                          customerView
                          hideConversationEntries
                          title="Status Updates"
                        />
                      </OrderCollapsibleSection>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        ))}
          </div>
        </section>
      )}

      {/* Inquiry Modal */}
      {showInquiryModal && selectedOrder && (
        <div className="modal-overlay" onClick={closeInquiryModal}>
          <div className="modal-content inquiry-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>❓ Ask a Question About Order #{selectedOrder.order_number}</h3>
              <button className="modal-close-btn" onClick={closeInquiryModal}>×</button>
            </div>

            <div className="modal-body">
              <div className="order-summary-box">
                <p><strong>Order Date:</strong> {formatOrderDate(selectedOrder.created_at_full || selectedOrder.created_at)}</p>
                <p><strong>Total:</strong> ${(selectedOrder.total / 100).toFixed(2)}</p>
                <p><strong>Status:</strong> <span className={`order-status ${getStatusColor(selectedOrder.status)}`}>{selectedOrder.status.toUpperCase()}</span></p>
              </div>

              <div className="form-group">
                <label htmlFor="inquiry-question">Your Question:</label>
                <textarea
                  id="inquiry-question"
                  className="inquiry-textarea"
                  rows="6"
                  placeholder="Please describe your question or concern about this order..."
                  value={inquiryQuestion}
                  onChange={(e) => setInquiryQuestion(e.target.value)}
                  disabled={submittingInquiry}
                />
              </div>

              {inquiryError && (
                <div className="error-message">
                  ❌ {inquiryError}
                </div>
              )}

              {inquirySuccess && (
                <div className="success-message">
                  ✅ {inquirySuccess}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={closeInquiryModal}
                disabled={submittingInquiry}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={submitInquiry}
                disabled={submittingInquiry || !inquiryQuestion.trim()}
              >
                {submittingInquiry ? 'Submitting...' : 'Submit Inquiry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderHistory;










